import React, { useEffect, useState, useRef } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Loader2, Download } from 'lucide-react';
import * as API from '../../../api';
import { isSuperAdmin } from '../../../modules/auth/core/Auth';

interface HelpModalProps {
  reportName: string;
  pdfUrl?: string;
  fileName?: string;
  onClose: () => void;
  onUploadSuccess: (pdfUrl: string) => void;
  onBusyChange?: (busy: boolean) => void;
}

interface UploadedManual {
  url: string;
  id: number | string;
  name: string;
}

export const HelpModal: React.FC<HelpModalProps> = ({
  reportName,
  pdfUrl,
  fileName,
  onClose,
  onUploadSuccess,
  onBusyChange
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [pendingManual, setPendingManual] = useState<UploadedManual | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canEdit = isSuperAdmin();
  const isBusy = isUploading || isSaving;
  const previewUrl = pendingManual?.url || pdfUrl;
  const previewName = pendingManual?.name || fileName;

  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canEdit || isBusy) return;

    setUploadSuccess(false);
    if (file.type !== 'application/pdf') {
      setUploadError('Por favor, selecione apenas arquivos PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('O arquivo deve ter no máximo 10MB');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const response = await API.uploadFile(file);
      if (response?.success !== true) {
        throw new Error('Não foi possível enviar o PDF. Tente novamente.');
      }

      const uploadedUrl =
        response?.data?.pdf_url ??
        response?.data?.url ??
        response?.data?.file_url ??
        response?.data?.path ??
        response?.pdf_url ??
        response?.url ??
        response?.file_url ??
        response?.path;
      const uploadId = response?.data?.id_upload;

      if (typeof uploadedUrl !== 'string' || !uploadedUrl.trim()) {
        throw new Error('Upload concluído, mas não foi retornada a URL do PDF');
      }
      if (
        !['number', 'string'].includes(typeof uploadId) ||
        !Number.isInteger(Number(uploadId)) ||
        Number(uploadId) <= 0
      ) {
        throw new Error('Upload concluído, mas não foi retornado o identificador do arquivo. Envie o PDF novamente.');
      }

      setPendingManual({url: uploadedUrl, id: uploadId, name: file.name});
    } catch (error: any) {
      setUploadError(error?.message || 'Erro ao fazer upload do arquivo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!pendingManual || !canEdit || isBusy) return;

    setIsSaving(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const response = await API.addReportPDF({
        type: reportName,
        id_upload: pendingManual.id,
      });
      if (response?.success !== true) {
        throw new Error('Não foi possível vincular o PDF ao relatório. Tente salvar novamente.');
      }

      // O relatório só deve atualizar o manual depois da confirmação do vínculo.
      onUploadSuccess(pendingManual.url);
      setPendingManual(null);
      setUploadSuccess(true);
    } catch (error: any) {
      setUploadError(error?.message || 'Erro ao salvar o manual. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="relative flex flex-col bg-white rounded-xl shadow-2xl w-full max-h-[90vh] overflow-hidden m-0">
      <div className="flex flex-shrink-0 items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 m-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              {previewUrl ? 'Manual de Ajuda' : 'Adicionar Manual de Ajuda'}
            </h2>
            <p className="text-blue-100 text-sm">{reportName}</p>
          </div>
        </div>
        <button
          type="button"
          aria-label="Fechar manual de ajuda"
          onClick={onClose}
          disabled={isBusy}
          className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      </div>

      <div className="min-h-0 overflow-y-auto p-6 space-y-6">
        {uploadError && (
          <div role="alert" className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">{uploadError}</p>
          </div>
        )}

        {uploadSuccess && (
          <div role="status" className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700">Manual salvo e vinculado ao relatório com sucesso!</p>
          </div>
        )}

        {isBusy && (
          <div role="status" className="flex items-center justify-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <p className="text-sm text-blue-700">
              {isUploading ? 'Fazendo upload do arquivo...' : 'Salvando manual...'}
            </p>
          </div>
        )}

        {pendingManual && (
          <p role="status" className="text-sm text-blue-700">
            PDF enviado. Confira o arquivo e clique em "Salvar e vincular ao relatório" para concluir.
          </p>
        )}

        {previewUrl ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex min-w-0 items-center gap-3">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="min-w-0">
                  {previewName && <p className="text-sm text-gray-900 break-words">{previewName}</p>}
                  <p className="text-xs text-gray-500">
                    {pendingManual ? 'Aguardando salvar o vínculo com o relatório' : 'PDF disponível para visualização'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
                className="inline-flex flex-shrink-0 items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Baixar PDF
              </button>
            </div>

            <div className="bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
              <iframe
                src={previewUrl}
                className="w-full h-[45vh] min-h-[200px]"
                title="Manual de Ajuda"
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum manual cadastrado
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {canEdit
                ? 'Faça upload de um arquivo PDF com instruções e documentação para este relatório'
                : 'O manual de ajuda ainda não está disponível para este relatório.'}
            </p>
          </div>
        )}

        {canEdit && (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
              className={previewUrl
                ? 'inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                : 'w-full border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'}
            >
              {previewUrl ? (
                <>
                  <Upload className="w-4 h-4" />
                  {pendingManual ? 'Selecionar outro PDF' : 'Atualizar PDF'}
                </>
              ) : (
                <span className="flex flex-col items-center gap-4">
                  <span className="p-4 bg-blue-100 rounded-full">
                    <Upload className="w-8 h-8 text-blue-600" />
                  </span>
                  <span>
                    <span className="block text-lg font-semibold text-gray-900 mb-1">
                      Clique para selecionar um arquivo
                    </span>
                    <span className="block text-sm text-gray-500">
                      Formato: PDF | Tamanho máximo: 10MB
                    </span>
                  </span>
                </span>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              aria-label="Selecionar PDF do manual"
              accept="application/pdf"
              onChange={handleFileSelect}
              disabled={isBusy}
              className="hidden"
            />
          </>
        )}
      </div>

      <div className="flex flex-shrink-0 flex-wrap justify-end gap-3 p-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onClose}
          disabled={isBusy}
          className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {pendingManual || !pdfUrl ? 'Cancelar' : 'Fechar'}
        </button>
        {canEdit && pendingManual && (
          <button
            type="button"
            onClick={handleSave}
            disabled={isBusy}
            className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSaving ? 'Salvando...' : 'Salvar e vincular ao relatório'}
          </button>
        )}
      </div>
    </div>
  );
};

export default HelpModal;
